/**
 * URL path segment for plan/epic routes: prefer dated pathSlug, else bare id.
 * Arg order is slug-first (unlike routes.* which keep id-first for call sites).
 */
export function pathToken(pathSlug?: string | null, id?: string | null): string {
  return (pathSlug || id || '').trim();
}

/**
 * Typed path builders for in-app navigation (no window.location).
 * Optional pathSlug makes shareable URLs; omitted slug falls back to bare id
 * so older bookmarks and API responses without pathSlug still resolve.
 */
export const routes = {
  /** Public SaaS landing (Signal Field). */
  landing: () => '/',
  /** Authenticated factory home (attention inbox). */
  home: () => '/home',
  /** Coding work board (default WIP); optional ?view= chip. */
  work: () => '/work/coding',
  workCoding: (view?: string) =>
    view ? `/work/coding?view=${encodeURIComponent(view)}` : '/work/coding',
  wip: () => '/work/coding?view=wip',
  backlog: () => '/work/coding?view=backlog',
  all: () => '/work/coding?view=all',
  done: () => '/work/coding?view=done',
  openPrs: () => '/work/coding?view=openPrs',
  pipelines: () => '/work/coding?view=pipelines',
  failedPipelines: () => '/work/coding?view=failed-pipelines',
  /** Support board (Slack-originated cards). */
  support: () => '/work/support',
  /** Bugs board (Slack ERROR + Bugsnag intake). */
  bugs: () => '/work/bugs',
  /** Per-repo deploy timeline for the active project. */
  deployments: (sort?: 'name' | 'activity') =>
    sort ? `/deployments?sort=${encodeURIComponent(sort)}` : '/deployments',
  /** Connections Slack tab (was Support Slack settings). */
  supportSettings: () => '/connections/slack',
  dispatch: () => '/dispatch',
  /** Factory roadmap (sequenced dependency graph). */
  roadmap: () => '/roadmap',
  roadmapDashboard: () => '/roadmap?view=dashboard',
  roadmapHorizons: () => '/roadmap?view=horizons',
  roadmapInitiative: (id: string) => `/roadmap/initiatives/${encodeURIComponent(id)}`,
  settings: () => '/settings',
  /** Automations list (Cursor-style). */
  automations: () => '/automations',
  automationNew: () => '/automations/new',
  automation: (id: string) => `/automations/${encodeURIComponent(id)}`,
  /** Event datalake browser (under Automations). */
  events: () => '/automations/events',
  /** Agents hub (External coding-agent registry + Built-in factory agents). */
  agents: () => '/agents',
  agentsExternal: (tab?: 'mine' | 'team') =>
    tab === 'team' ? '/agents/external?tab=team' : '/agents/external',
  /** Open create-agent modal on the External list (optional ownership tab). */
  agentsExternalNew: (tab?: 'mine' | 'team') => {
    const sp = new URLSearchParams();
    if (tab === 'team') sp.set('tab', 'team');
    sp.set('modal', 'new');
    return `/agents/external?${sp.toString()}`;
  },
  /** Open edit-agent modal on the External list. */
  agentsExternalEdit: (id: string, tab?: 'mine' | 'team') => {
    const sp = new URLSearchParams();
    if (tab === 'team') sp.set('tab', 'team');
    sp.set('edit', id);
    return `/agents/external?${sp.toString()}`;
  },
  agentsBuiltin: () => '/agents/builtin',
  agentsBuiltinAgent: (agentKey: string) =>
    `/agents/builtin/${encodeURIComponent(agentKey)}`,
  /** Factory Agent Console (owner → tracked Cursor agents). */
  console: () => '/console',
  codingAgents: () => '/agents/external',
  /** Deep link → list create modal (legacy path kept for bookmarks). */
  codingAgentNew: () => '/agents/new',
  /** Deep link → list edit modal (legacy path kept for bookmarks). */
  codingAgent: (id: string) => `/agents/${encodeURIComponent(id)}`,
  /** Public assimilation guide for coding agents. */
  forAgents: () => '/for-agents',
  /** Connections hub (Git / Slack / Alerts). */
  connections: (tab?: 'git' | 'slack' | 'alerts') =>
    tab ? `/connections/${tab}` : '/connections',
  /** Git providers list (Connections → Git). */
  gitRepos: () => '/connections/git',
  /** Open create-provider modal on the Git list (`?modal=new`; optional OAuth `git` flash). */
  gitRepoNew: (options?: { git?: string }) => {
    const sp = new URLSearchParams({ modal: 'new' });
    if (options?.git) sp.set('git', options.git);
    return `/connections/git?${sp.toString()}`;
  },
  /** Open edit-provider modal on the Git list (`?edit=:id`; optional OAuth `git` flash). */
  gitRepo: (id: string, options?: { git?: string }) => {
    const sp = new URLSearchParams({ edit: id });
    if (options?.git) sp.set('git', options.git);
    return `/connections/git?${sp.toString()}`;
  },
  /** Alias of gitRepo — kept for call sites that mirror agentsExternalEdit naming. */
  gitRepoEdit: (id: string, options?: { git?: string }) => routes.gitRepo(id, options),
  /** Project infrastructure status (live checks). */
  status: () => '/status',
  /** Section/check catalog for infrastructure monitoring. */
  statusConfig: () => '/status/config',
  /** Team admin. */
  team: () => '/team',
  teamMembers: () => '/team/members',
  teamProjects: () => '/team/projects',
  teamProject: (id: string) => `/team/projects/${encodeURIComponent(id)}`,
  /** Comms discussion board (agent↔human). */
  comms: () => '/comms',
  commsConversation: (id: string) => `/comms/${encodeURIComponent(id)}`,
  /** Living User Stories product intent browser. */
  stories: (projectId?: string) =>
    projectId ? `/stories/${encodeURIComponent(projectId)}` : '/stories',
  storyDocument: (projectId: string, documentId: string) =>
    `/stories/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}`,
  epic: (id: string, pathSlug?: string | null) => `/epics/${pathToken(pathSlug, id)}`,
  login: () => '/login',
  /** Plan tab — explicit `/plan` suffix (bare `/plans/:id` redirects here). */
  plan: (id: string, pathSlug?: string | null) => `/plans/${pathToken(pathSlug, id)}/plan`,
  /** Diff tab. `/review` redirects here so older board links keep working. */
  diff: (id: string, pathSlug?: string | null) => `/plans/${pathToken(pathSlug, id)}/diff`,
  pipeline: (id: string, pathSlug?: string | null) => `/plans/${pathToken(pathSlug, id)}/pipeline`,
  /** Card History tab — unified event log for this plan. */
  history: (id: string, pathSlug?: string | null) => `/plans/${pathToken(pathSlug, id)}/history`,
  /** Named markdown artifacts under the plan (HumanLayer-style task hub). */
  artifacts: (id: string, pathSlug?: string | null) =>
    `/plans/${pathToken(pathSlug, id)}/artifacts`,
  artifact: (id: string, artifactId: string, pathSlug?: string | null) =>
    `/plans/${pathToken(pathSlug, id)}/artifacts/${encodeURIComponent(artifactId)}`,
  review: (id: string, pathSlug?: string | null) => `/plans/${pathToken(pathSlug, id)}/review`,
};
